<template>
    <div class="fert-report" :class="reportColorClass">
        <print-page-header :is-loading="!treatment || !patient" />

        <div v-if="treatment && patient" class="print_content portrait">
            <report-page-template
                v-for="(page, pageIndex) in pages"
                :key="pageIndex"
                :report-title="reportName"
                :page-num="pageIndex + 1"
                :total-pages="pages.length"
                @onReportTitleChange="setNewReportName"
            >
                <!-- first page only -->
                <template v-if="pageIndex === 0">
                    <report-id
                        :patient-id="patientId"
                        :treatment-id="treatmentId"
                        :treatment-start-date="treatment.startDate"
                    />
                    <fert-cycle-overview :wells="wells" />
                </template>

                <div v-if="!isFdaFriendly" class="embryo-development">
                    <!-- first page only -->
                    <div v-if="pageIndex === 0" class="embryo-development-header">
                        <span class="title">
                            {{ $translate('embryo_development') }} - {{ $translate('day1') }}
                        </span>
                        <div class="default-type">
                            {{ $translate('treatment_type') }} :
                            <fertilization-type-select
                                :default-type="masterFertType"
                                class="field"
                                @change="setMasterFertType($event)"
                            />
                        </div>
                    </div>

                    <!-- split to many pages -->
                    <ul class="embryo-development-list">
                        <li v-for="(well, idx) in page.wells" :key="idx">
                            <embryo-fert-card
                                :well="well"
                                :default-fert-type="getDefaultFertTypeByWell(well)"
                            />
                        </li>
                    </ul>
                </div>

                <!-- last page only -->
                <template v-if="isLastPage(pageIndex) && !closingNeedsExtraPage">
                    <pn-legend v-if="!isFdaFriendly" />
                    <closing-part />
                </template>
            </report-page-template>

            <report-page-template
                v-if="closingNeedsExtraPage"
                :report-title="reportName"
                :page-num="totalPages"
                :total-pages="totalPages"
                class="printed-notes"
            >
                <pn-legend v-if="!isFdaFriendly" />
                <closing-part />
            </report-page-template>
        </div>
    </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import UIUtils from '../../../business/utils/UIUtils'
import PrintPageHeader from '../../components/print/PrintPageHeader'
import ReportPageTemplate from '../../components/report/ReportPageTemplate'
import ReportId from '../../components/report/ReportId'
import FertCycleOverview from '../../components/report/fert-report/FertCycleOverview'
import EmbryoFertCard from '../../components/report/fert-report/EmbryoFertCard'
import PnLegend from '../../components/report/fert-report/PnLegend'
import FertilizationTypeSelect from '../../components/report/FertilizationTypeSelect.vue'
import Utils from '../../../business/utils/Utils'
import ClosingPart from '../../components/report/cycle-report/ClosingPart'

const PAGE_BODY = 1172 // without fda-disclaimer 1125

const REPORT_ID = 71
const CYCLE_OVERVIEW = 177
const EMBRYO_DEV_TITLE = 40

const LAST_PAGE_COMMENT = 99

const AVAILABLE_HEIGHT_BY_PAGE = {
    firstPage: PAGE_BODY - (REPORT_ID + CYCLE_OVERVIEW + EMBRYO_DEV_TITLE),
    default: PAGE_BODY
}
const CARD_ROW_HEIGHT = 170
const CARDS_IN_ROW = 3

const SIGNATURES_MIN_HEIGHT = 80

export default {
    name: 'Report',
    components: {
        FertilizationTypeSelect,
        ReportPageTemplate,
        PnLegend,
        PrintPageHeader,
        ReportId,
        FertCycleOverview,
        EmbryoFertCard,
        ClosingPart
    },
    data: () => ({
        reportName: '',
        masterFertType: 'na'
    }),
    computed: {
        ...mapState({
            treatment: state => state.timelapse.selectedTreatment,
            patient: state => state.timelapse.selectedPatient,
            tenant: state => state.common.selectedTenant
        }),
        ...mapGetters({
            isFdaFriendly: 'common/isFdaFriendly',
            getPnCount: 'timelapse/getPnCount'
        }),
        wells() {
            if (!this.treatment) return []
            return this.treatment.wellsByIndex
        },
        pages() {
            const wellsWith2PN = this.wells.filter(well => this.getPnCount(well) === 2)
            const cardRowsNum = Math.ceil(wellsWith2PN.length / CARDS_IN_ROW)

            // splitting fertilized wells into rows
            const rows = []

            for (let rowIdx = 0; rowIdx < cardRowsNum; rowIdx++) {
                const rowStartIdx = rowIdx * CARDS_IN_ROW
                const row = [wellsWith2PN[rowStartIdx]]

                for (let cardIdx = 1; cardIdx < CARDS_IN_ROW; cardIdx++) {
                    if (wellsWith2PN[rowStartIdx + cardIdx]) {
                        row.push(wellsWith2PN[rowStartIdx + cardIdx])
                    }
                }
                rows.push(row)
            }

            // splitting rows into pages
            const pages = [
                {
                    availableHeight: AVAILABLE_HEIGHT_BY_PAGE.firstPage,
                    wells: []
                }
            ]
            // keeping single page for FDA-friendly version
            if (this.isFdaFriendly) return pages

            rows.forEach((row, idx) => {
                const currPage = pages.at(-1)

                const isLastRow = idx === rows.length - 1
                const neededSpace = isLastRow
                    ? CARD_ROW_HEIGHT + LAST_PAGE_COMMENT
                    : CARD_ROW_HEIGHT

                if (neededSpace <= currPage.availableHeight) {
                    currPage.wells.push(...row)
                    currPage.availableHeight = currPage.availableHeight - neededSpace
                } else {
                    pages.push({
                        availableHeight: AVAILABLE_HEIGHT_BY_PAGE.default - neededSpace,
                        wells: [...row]
                    })
                }
            })

            return pages
        },
        closingNeedsExtraPage() {
            const availableHeight = this.pages.at(-1).availableHeight

            return SIGNATURES_MIN_HEIGHT > availableHeight
        },
        totalPages() {
            return this.closingNeedsExtraPage ? this.pages.length + 1 : +this.pages.length
        },
        reportColorClass() {
            return this.tenant.color || 'black'
        },
        patientId() {
            return this.$utils.getPatientEsIdByVendor(
                this.treatment.instrumentType[0],
                this.patient
            )
        },
        treatmentId() {
            const treatmentDisplayIdsArray = this.treatment.slides.map(_slide => _slide.treatment.esId)
            const treatmentDisplayIdsSet = new Set(treatmentDisplayIdsArray)
            return [...treatmentDisplayIdsSet].join(', ')
        }
    },
    async created() {
        await this.setTreatmentData()
        this.setDefaultReportName()
    },
    methods: {
        async setTreatmentData() {
            UIUtils.showLoadingMask()

            const { treatment_id, patient_id } = this.$route.params

            await this.$store.dispatch('timelapse/loadPatientData', patient_id)
            await this.$store.dispatch('timelapse/setSelectedTreatment', treatment_id)
            await this.setLocale()

            this.setMasterFertTypeIVFIfPossible()
            UIUtils.hideLoadingMask()
        },
        setDefaultReportName() {
            this.reportName = this.$translate('fertilization_report')
        },
        isLastPage(pageIndex) {
            return pageIndex === this.pages.length - 1
        },
        setNewReportName(newReportName) {
            this.reportName = newReportName
        },
        setMasterFertType(type) {
            this.masterFertType = type
        },
        setLocale() {
            const { language } = this.tenant
            if (language) {
                this.$store.commit('locale/setLocale', language)
            }
        },
        setMasterFertTypeIVFIfPossible() {
            const IVF_WELL_VIDEO_START_HOUR = 10.0
            const allWellsVideoStartAfterIVFHour = this.wells.every(
                well => well && well.runs[0].time >= IVF_WELL_VIDEO_START_HOUR
            )
            if (allWellsVideoStartAfterIVFHour) {
                this.masterFertType = 'ivf'
            }
        },
        getDefaultFertTypeByWell(well) {
            const wellType = Utils.getFertTypeByWell(well)

            return this.masterFertType !== 'na' ? this.masterFertType : wellType
        }
    }
}
</script>

<style lang="scss" scoped>
@page {
    size: A4;
    margin: 0;
}

.fert-report {
    display: block;
    position: relative;
    padding-top: 100px;
    min-height: 100vh;
    color: $light-3;

    .print_content {
        position: relative;
        display: block;
        margin: 0 auto;
        width: 100%;
        min-height: 1122.519685px; // when using mm the result isn't consistent per screen

        @media print {
            width: 100%;
            height: 100%;
            margin: 0;
            padding: 0;
            page-break-before: always;
        }

        .embryo-development {
            .embryo-development-header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 10px;

                .default-type {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    gap: 1rem;

                    @media print {
                        display: none;
                    }
                }
            }

            .title {
                font-size: 18px;
                font-weight: 600;
            }

            .embryo-development-list {
                list-style-type: none;
                padding: 0;

                display: grid;
                grid-template-columns: repeat(3, 1fr);
                column-gap: 20px;
                row-gap: 15px;
            }
        }

        .last-page-only {
            margin-top: auto;

            .about-chloe {
                margin-top: 15px;
            }
        }
    }

    @media print {
        background: none;
        color: black;
        margin: 0;
        padding: 0;

        * {
            -webkit-print-color-adjust: exact;
        }
    }
}
</style>
